"use client";

import { CHART_COLORS } from "@/components/charts/event-chart";
import { formatCentPriceAsChancePercent } from "@/utils/price";
import { get } from "@/utils/utils";

interface LegendFilterDialogProps {
    open: boolean;
    guessList: any[];
    selectedGuessNos: string[];
    onSelectionChange: ( selectedGuessNos: string[] ) => void;
}

// 选中状态的SVG图标
function CheckedIcon( { color }: { color: string } ) {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.11 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
                fill={color}
            />
        </svg>
    );
}

// 未选中状态的SVG图标
function UncheckedIcon() {
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M19 5V19H5V5H19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z"
                fill="black"
                fillOpacity="0.35"
            />
        </svg>
    );
}

export function LegendFilterDialog(
    { open, guessList, selectedGuessNos, onSelectionChange }: LegendFilterDialogProps,
) {
    const maxSelection = 4;

    // 按 status 从小到大排序
    const sortedGuessList = [ ...guessList ].sort( ( a, b ) => {
        const aStatus = get( a, "status", 0 );
        const bStatus = get( b, "status", 0 );
        return aStatus - bStatus;
    } );

    const handleToggleGuess = ( guessNo: string ) => {
        const isSelected = selectedGuessNos.includes( guessNo );

        if ( isSelected ) {
            // 取消选择 - 如果只剩1项，不允许取消
            if ( selectedGuessNos.length === 1 ) {
                return;
            }
            onSelectionChange( selectedGuessNos.filter( ( no ) => no !== guessNo ) );
        } else {
            // 选择 - 如果已达到最大数量，不允许再选择
            if ( selectedGuessNos.length >= maxSelection ) {
                return;
            }
            onSelectionChange( [ ...selectedGuessNos, guessNo ] );
        }
    };

    // 根据 guessNo 在原始 guessList 中的索引确定颜色
    const getGuessColor = ( guessNo: string ) => {
        const index = guessList.findIndex( ( g ) => get( g, "guessNo", "" ) === guessNo );
        return index >= 0 ? CHART_COLORS[index % CHART_COLORS.length] : CHART_COLORS[0];
    };

    if ( !open ) {
        return null;
    }

    return (
        <div
            className="absolute bottom-[41px] left-0 right-0 bg-surface-card rounded-lg shadow-[0px_0px_8px_0px_rgba(0,0,0,0.1),0px_8px_16px_0px_rgba(0,0,0,0.1)] z-50 max-w-[calc(100vw-32px)]"
            onMouseDown={( e ) => {
                // Prevent click events from bubbling up
                e.preventDefault();
            }}
        >
            {/* Header */}
            <div className="px-4 pt-4 pb-2">
                <h3 className="text-sm font-normal text-t1">Pick up to 4 markets</h3>
            </div>

            {/* Options list */}
            <div className="px-4 pb-3 max-h-[260px] overflow-y-auto">
                <div className="flex flex-col">
                    {sortedGuessList.map( ( guess ) => {
                        const guessNo = get( guess, "guessNo", "" );
                        const title = get( guess, "title", "" );
                        const status = get( guess, "status", 0 );
                        const percentage = get( guess, "yesRealtimePrice", 0 )
                            ? formatCentPriceAsChancePercent( get( guess, "yesRealtimePrice" ), 1 )
                            : "-";
                        const isSelected = selectedGuessNos.includes( guessNo );
                        const color = getGuessColor( guessNo );
                        // 当竞猜项>=4时，status 为 3 的项不可选
                        const isDisabled = ( guessList.length >= 4 && status === 3 )
                            || ( !isSelected && selectedGuessNos.length >= maxSelection )
                            || ( !isSelected && selectedGuessNos.length === 0 );

                        return (
                            <button
                                key={guessNo}
                                onClick={() => handleToggleGuess( guessNo )}
                                disabled={isDisabled}
                                className={`
                                    flex items-center gap-3 pl-2 pr-4 rounded-lg h-10
                                    transition-colors text-left
                                    ${
                                    isDisabled
                                        ? "opacity-50 cursor-not-allowed"
                                        : "cursor-pointer hover:bg-surface-subtle"
                                }
                                `}
                            >
                                {/* Checkbox - 使用新的SVG图标 */}
                                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                                    {isSelected ? <CheckedIcon color={color} /> : <UncheckedIcon />}
                                </div>

                                {/* Title */}
                                <span className="flex-1 text-sm font-normal text-t1 truncate">{title}</span>

                                {/* Percentage */}
                                <span className="text-sm font-medium text-t1 flex-shrink-0">{percentage}</span>
                            </button>
                        );
                    } )}
                </div>
            </div>
        </div>
    );
}
